import React, { memo, useState } from 'react';
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Chip,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme
} from '@mui/material';
import SplitPane, { Pane } from 'split-pane-react';
import 'split-pane-react/esm/themes/default.css';
import '../../css/splitPaneDivider.css';
import WarningIcon from '@mui/icons-material/Warning';
import BaseModal from './BaseModal';
import { useDetailRequest } from '../../context/DetailRequestContext';
import { getSentence } from '../translations';
import Editor from '../Editor';

/**
 * DetailRequestModal - 2-step modal for detail request enhancement
 * 
 * Step 1: Credit warning and confirmation
 * - Shows original content
 * - Warns about credit consumption
 * - Asks for user confirmation
 * 
 * Step 2: Side-by-side editing
 * - Shows original content (read-only)
 * - Shows enhanced content (editable)
 * - Allows replace or cancel
 */
const DetailRequestModal = memo(() => {
  const { 
    isModalOpen, 
    modalStep,
    modalData, 
    closeModal,
    proceedToEnhancement,
    updateEditableContent,
    applyEnhancedContent 
  } = useDetailRequest();

  const { 
    title, 
    originalContent, 
    enhancedContent,
    editableContent,
    isLoading, 
    error 
  } = modalData;

  // Local state for editing in step 2
  const [localEditableContent, setLocalEditableContent] = useState('');

  //editorWithNewContent with the new content
  const [editorWithNewContent, setEditorWithNewContent] = useState('');

  // Tab state for narrow screens
  const [activeTab, setActiveTab] = useState(0);
  
  // Split pane sizes for side-by-side layout
  const [splitSizes, setSplitSizes] = useState(['50%', '50%']);
  
  // Media query to detect narrow screens
  const theme = useTheme();
  const isNarrowScreen = useMediaQuery(theme.breakpoints.down('lg')); // Less than 1200px


  const updateDoNothing = async (value) => {};

  const editorWithOriginalContent = Editor( originalContent || 'No content available', 
                updateDoNothing, 
                false, 
                false, 
                false, 
                true, 
                false,
                null,
                1
              )


  // Update local editable content when enhanced content is available
  React.useEffect(() => {
    if (editableContent && modalStep === 2) {
      setLocalEditableContent(editableContent);
      setEditorWithNewContent(Editor(
                      editableContent, 
                      handleContentChange, 
                      false, 
                      false, 
                      true, 
                      true, 
                      false,
                      null,
                      1
                    ))
      // When moving to step 2 with enhanced content, switch to enhanced tab
      if (isNarrowScreen) {
        setActiveTab(1); // Switch to enhanced content tab
      }
    }
  }, [editableContent, modalStep, isNarrowScreen]);

  // Reset tab to original content when modal opens or returns to step 1
  React.useEffect(() => {
    if (isModalOpen && modalStep === 1) {
      setActiveTab(0); // Start with original content tab
    }
  }, [isModalOpen, modalStep]);

  // Handle close - just close the modal, don't apply changes
  const handleClose = () => {
    setLocalEditableContent(''); // Reset local state
    setActiveTab(0); // Reset to original content tab
    closeModal();
  };

  // Handle proceed from step 1 to step 2
  const handleProceed = () => {
    proceedToEnhancement();
  };

  // Handle content editing in step 2
  const handleContentChange = async (newValue) => {
    const newContent = newValue;
    setLocalEditableContent(newContent);
    updateEditableContent(newContent);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle split pane size changes
  const handleSplitChange = (sizes) => {
    setSplitSizes(sizes);
  };

  // Handle apply - this triggers the content replacement via callback
  const handleApply = () => {
    applyEnhancedContent();
  };

  // Dynamic actions based on current step
  const getActions = () => {
    if (modalStep === 1) {
      // Step 1: Confirmation actions
      return (
        <>
          <Button 
            onClick={handleClose} 
            variant="outlined"
            disabled={isLoading}
            sx={{ 
              textTransform: 'none',
              minWidth: { xs: 'auto', sm: '100px' }
            }}
          >
            {getSentence(8)}
          </Button>
          <Button 
            onClick={handleProceed} 
            variant="contained"
            disabled={isLoading}
            sx={{ 
              textTransform: 'none',
              minWidth: { xs: 'auto', sm: '120px' }
            }}
          >
            {isLoading ? getSentence(171) : getSentence(172)}
          </Button>
        </>
      );
    } else {
      // Step 2: Edit and apply actions
      return (
        <>
          <Button 
            onClick={handleClose} 
            variant="outlined"
            disabled={isLoading}
            sx={{ 
              textTransform: 'none',
              minWidth: { xs: 'auto', sm: '100px' }
            }}
          >
            {getSentence(49)}
          </Button>
          <Button 
            onClick={handleApply} 
            variant="contained"
            disabled={isLoading || !localEditableContent.trim()}
            sx={{ 
              textTransform: 'none',
              minWidth: { xs: 'auto', sm: '100px' }
            }}
          >
            {getSentence(173)}
          </Button>
        </>
      );
    }
  };

  // Dynamic title based on current step
  const getTitle = () => {
    if (modalStep === 1) {
      return getSentence(175) + `: ${title}`;
    } else {
      return getSentence(176) + `: ${title}`;
    }
  };

  return (
    <BaseModal
      open={isModalOpen}
      onClose={handleClose}
      title={getTitle()}
      size="medium"
      maxWidth={modalStep === 2 ? (isNarrowScreen ? "md" : "xl") : "md"}
      actions={getActions()}
      aria-labelledby="detail-request-modal-title"
      aria-describedby="detail-request-modal-description"
    >
      <Box id="detail-request-modal-description">
        {modalStep === 1 ? (
          // STEP 1: Credit Warning and Confirmation
          <>
            {/* Warning Section */}
            <Alert 
              severity="warning" 
              icon={<WarningIcon />}
              sx={{ mb: 3 }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{getSentence(166)}</strong>
              </Typography>
              <Typography variant="body2">
                {getSentence(167)}
              </Typography>
            </Alert>

            {/* Original Content Preview */}
            <Box mb={3}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                  {getSentence(168)}
                </Typography>
                <Chip 
                  label={`${originalContent.length} ` + getSentence(174)} 
                  size="small" 
                  variant="outlined" 
                />
              </Box>
              <Box
                sx={{
                  backgroundColor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 1,
                  p: 2,
                  maxHeight: '300px',
                  overflowY: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap'
                }}
              >

              {editorWithOriginalContent}

              </Box>
            </Box>
            
            {/* Loading State for Step 1 */}
            {isLoading && (
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="center" 
                py={3}
                gap={2}
              >
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  {getSentence(169)}
                </Typography>
              </Box>
            )}

            {/* Error State for Step 1 */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Error:</strong> {error}
                </Typography>
              </Alert>
            )}
          </>
        ) : (
          // STEP 2: Tabbed vs Side-by-side editing based on screen width
          <>
            {isNarrowScreen ? (
              // NARROW SCREEN: Tabbed interface
              <Box sx={{ width: '100%' }}>
                <Tabs 
                  value={activeTab} 
                  onChange={handleTabChange} 
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                >
                  <Tab label="Original Content" />
                  <Tab label="Enhanced Content" />
                </Tabs>

                {/* Tab Panel 0: Original Content */}
                {activeTab === 0 && (
                  <Box sx={{ height: '450px', display: 'flex', flexDirection: 'column' }}>
                    <Box
                      sx={{
                        backgroundColor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'grey.200',
                        borderRadius: 1,
                        p: 2,
                        height: '400px',
                        minHeight: '400px',
                        maxHeight: '400px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        scrollBehavior: 'smooth',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)',
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: 'grey.100',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'grey.400',
                          borderRadius: '4px',
                          '&:hover': {
                            backgroundColor: 'grey.500',
                          },
                        },
                      }}
                    >
                      {editorWithOriginalContent}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      {originalContent.length} characters (read-only)
                    </Typography>
                  </Box>
                )}

                {/* Tab Panel 1: Enhanced Content */}
                {activeTab === 1 && (
                  <Box
                      sx={{
                        backgroundColor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'grey.200',
                        borderRadius: 1,
                        p: 2,
                        height: '400px',
                        minHeight: '400px',
                        maxHeight: '400px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        scrollBehavior: 'smooth',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)',
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: 'grey.100',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'grey.400',
                          borderRadius: '4px',
                          '&:hover': {
                            backgroundColor: 'grey.500',
                          },
                        },
                      }}
                    >
                      {editorWithNewContent}
                  </Box>
                )}
              </Box>
            ) : (
              // WIDE SCREEN: Resizable side-by-side layout
              <Box 
                sx={{ 
                  height: '480px',
                  maxHeight: '480px',
                  width: '100%',
                  overflow: 'hidden'
                }}
              >
                <SplitPane
                  split="vertical"
                  sizes={splitSizes}
                  onChange={handleSplitChange}
                  style={{ height: '100%' }}
                >
                  <Pane>
                    {/* Original Content (Read-only) */}
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pr: 1.5 }}>
                      <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary' }}>
                        {getSentence(164)}
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor: 'grey.50',
                          border: '1px solid',
                          borderColor: 'grey.200',
                          borderRadius: 1,
                          p: 2,
                          flex: 1,
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          whiteSpace: 'pre-wrap',
                          scrollBehavior: 'smooth',
                          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)',
                          '&::-webkit-scrollbar': {
                            width: '8px',
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: 'grey.100',
                            borderRadius: '4px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'grey.400',
                            borderRadius: '4px',
                            '&:hover': {
                              backgroundColor: 'grey.500',
                            },
                          },
                        }}
                      >
                        {editorWithOriginalContent}
                      </Box>
                    </Box>
                  </Pane>

                  <Pane>
                    {/* Enhanced Content (Editable) */}
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pl: 1.5 }}>
                      <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary' }}>
                        {getSentence(165)}
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor: 'grey.50',
                          border: '1px solid',
                          borderColor: 'grey.200',
                          borderRadius: 1,
                          p: 2,
                          flex: 1,
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          whiteSpace: 'pre-wrap',
                          scrollBehavior: 'smooth',
                          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)',
                          '&::-webkit-scrollbar': {
                            width: '8px',
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: 'grey.100',
                            borderRadius: '4px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'grey.400',
                            borderRadius: '4px',
                            '&:hover': {
                              backgroundColor: 'grey.500',
                            },
                          },
                        }}
                      >
                        {editorWithNewContent}
                      </Box>
                    </Box>
                  </Pane>
                </SplitPane>
              </Box>
            )}

            {/* Loading State for Step 2 */}
            {isLoading && (
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="center" 
                py={3}
                gap={2}
                sx={{ mt: 2 }}
              >
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Processing...
                </Typography>
              </Box>
            )}

            {/* Error State for Step 2 */}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Error:</strong> {error}
                </Typography>
              </Alert>
            )}
          </>
        )}

        {/* Debug Information (for development) */}
        {process.env.NODE_ENV === 'development' && (
          <Box mt={2} sx={{ opacity: 0.7 }}>
            <Typography variant="caption" color="text.secondary">
              Debug Info: Step {modalStep}, Loading: {isLoading.toString()}, 
              Has Enhanced: {!!enhancedContent}, Has Error: {!!error}
              {modalStep === 2 && (
                <>
                  <br />
                  Modal MaxWidth: xl, Window Width: {window.innerWidth}px
                </>
              )}
            </Typography>
          </Box>
        )}
      </Box>
    </BaseModal>
  );
});

DetailRequestModal.displayName = 'DetailRequestModal';

export default DetailRequestModal;
